import type { RemovableRef } from '@vueuse/core'
import { debounce } from 'lodash'
import * as Sentry from '@sentry/browser'
import type { Device } from '@twilio/voice-sdk'

export function useAudioSources (callDevice: Ref<Device | null>) {
  const snackbar = useSnackbar()

  const storedInputSource: RemovableRef<string> = useLocalStorage(
    'inputSource',
    'default'
  )
  const storedOutputSource: RemovableRef<string> = useLocalStorage(
    'outputSource',
    'default'
  )
  const availableInputSources = ref<MediaDeviceInfo[]>([])
  const inputSource = ref<string | null>(null)
  const availableOutputSources = ref<MediaDeviceInfo[]>([])
  const outputSource = ref<string | null>(null)

  const removeAudioSources = () => {
    callDevice.value?.audio?.removeAllListeners()
    availableInputSources.value = []
    inputSource.value = null
    availableOutputSources.value = []
    outputSource.value = null
  }

  // Twilio doesn't serialize setInputDevice and unsetInputDevice, so an unset still resolving
  // would wipe the stream a newer set just opened. Every input change waits for the previous one
  let inputDeviceQueue = Promise.resolve()
  const queueInputDeviceChange = (change: () => Promise<void>) => {
    const result = inputDeviceQueue.then(change)
    inputDeviceQueue = result.catch(() => {})
    return result
  }

  // Bumped on every release, so a setup requested before it doesn't set the mic up again
  let inputGeneration = 0

  const setAudioSources = () => {
    const generation = inputGeneration
    return queueInputDeviceChange(async () => {
      try {
        // Device labels are only exposed once permission is granted. The function setInputDevice opens
        // the stream that the calls actually use, so the probe's own tracks are dropped right away
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true })
        probe.getTracks().forEach(track => track.stop())
        if (generation !== inputGeneration) { return }
        if (!callDevice.value?.audio) {
          throw new Error('Device audio not found')
        }
        callDevice.value?.audio.removeAllListeners()
        callDevice.value?.audio.on('deviceChange', () => {
          // Skipped while released, re-running would reopen the mic between calls
          if (!inputSource.value) { return }
          setAudioSourcesDebounced()
        })
        availableInputSources.value = Array.from(
          callDevice.value?.audio.availableInputDevices.values()
        )
        const isStoredInputSourceAvailable = availableInputSources.value.some(
          (source) => {
            return source.deviceId === storedInputSource.value
          }
        )
        inputSource.value = isStoredInputSourceAvailable
          ? storedInputSource.value
          : 'default'
        availableOutputSources.value = Array.from(
          callDevice.value?.audio.availableOutputDevices.values()
        )
        const isStoredOutputSourceAvailable = availableOutputSources.value.some(
          (source) => {
            return source.deviceId === storedOutputSource.value
          }
        )
        outputSource.value = isStoredOutputSourceAvailable
          ? storedOutputSource.value
          : 'default'
        await Promise.all([
          callDevice.value?.audio.setInputDevice(inputSource.value),
          callDevice.value?.audio.speakerDevices.set(outputSource.value),
          callDevice.value?.audio.ringtoneDevices.set(outputSource.value)
        ])
      } catch (error) {
        Sentry.captureException(error)
        snackbar.error('Failed to get audio sources')
        removeAudioSources()
      }
    })
  }

  const setAudioSourcesDebounced = debounce(setAudioSources, 1000)

  // Twilio keeps the stream setInputDevice opened for the whole Device lifetime,
  // so the browser tab's "recording" indicator stays there after a call ends
  const releaseInputSource = () => {
    setAudioSourcesDebounced.cancel()
    inputGeneration++
    // Cleared now rather than once the unset resolves, so a call started in between
    // sees the mic as released and queues its setup behind the unset
    inputSource.value = null
    return queueInputDeviceChange(async () => {
      try {
        await callDevice.value?.audio?.unsetInputDevice()
      } catch (error) {
        Sentry.captureException(error)
      }
    })
  }

  const changeInputSource = (deviceId: string) => queueInputDeviceChange(async () => {
    try {
      await callDevice.value?.audio?.setInputDevice(deviceId)
      storedInputSource.value = deviceId
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to set input source')
    }
  })

  const changeOutputSource = async (deviceId: string) => {
    try {
      await Promise.all([
        callDevice.value?.audio?.speakerDevices.set(deviceId),
        callDevice.value?.audio?.ringtoneDevices.set(deviceId)
      ])
      storedOutputSource.value = deviceId
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to set output source')
    }
  }

  return {
    storedInputSource,
    storedOutputSource,
    availableInputSources,
    inputSource,
    availableOutputSources,
    outputSource,
    removeAudioSources,
    releaseInputSource,
    setAudioSources,
    setAudioSourcesDebounced,
    changeInputSource,
    changeOutputSource
  }
}
